import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import PropertyCard from "@/components/property-card";
import VillaConfigModal from "@/components/villa-config-modal";
import type { Property } from "@shared/schema";
import { queryConfigs } from "@/lib/queryClient";
import initialProperties from "@/data/initial-properties.json";

/** Shared live listing; page shells own the hero, navigation and footer. */
export default function VillaListing() {
  const [villaConfigOpen, setVillaConfigOpen] = useState(false);
  const { data: properties, isLoading, error } = useQuery<Property[]>({
    queryKey: ['/api/properties'],
    ...queryConfigs.properties,
    staleTime: 0,
    placeholderData: initialProperties as unknown as Property[],
    queryFn: async () => {
      const early = (window as any).__earlyData?.properties;
      if (early) {
        (window as any).__earlyData.properties = null;
        const data = await early;
        if (data) return data;
      }
      const res = await fetch('/api/properties', { credentials: 'include' });
      if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
      return res.json();
    },
  });

  return (
    <section id="villas" aria-labelledby="villas-heading" className="pt-12 sm:pt-16 lg:pt-20 pb-8 sm:pb-10 lg:pb-12 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 sm:mb-16">
          <h1 id="villas-heading" className="text-4xl md:text-5xl text-toka-text mb-6" style={{ fontFamily: 'Lato', fontWeight: '300' }}>VILLAS</h1>
          <p className="text-lg sm:text-xl text-toka-text max-w-3xl mx-auto font-light" style={{ fontFamily: 'Lato', fontWeight: '300' }}>Award winning, luxury, self-service accommodation. Each offering stunning views, premium amenities, thoughtful design, artfully created decor and premium comfort.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          {isLoading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="property-listing" aria-label="Loading villa">
                <div className="aspect-[4/3] bg-gray-200 animate-pulse rounded-lg"></div>
                <div className="pt-3 sm:pt-4">
                  <div className="h-5 sm:h-6 bg-gray-200 rounded mb-2 animate-pulse w-3/4"></div>
                  <div className="h-4 bg-gray-200 rounded mb-2 animate-pulse w-1/2"></div>
                  <div className="h-4 bg-gray-200 rounded animate-pulse w-full"></div>
                </div>
              </div>
            ))
          ) : error ? (
            <div className="col-span-full text-center py-12">
              <div className="max-w-md mx-auto">
                <div className="mb-6">
                  <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-4xl">🏠</span>
                  </div>
                </div>
                <h2 className="text-xl font-semibold text-toka-text mb-2">Properties temporarily unavailable</h2>
                <p className="text-toka-text mb-6">We're currently updating our property listings. Please try again in a moment.</p>
                <button onClick={() => window.location.reload()} className="bg-toka-primary hover:bg-toka-primary/90 text-white px-6 py-3 rounded-lg font-semibold">
                  Try Again
                </button>
              </div>
            </div>
          ) : properties && properties.length > 0 ? (
            properties.map((property, index) => (
              <PropertyCard key={property.id} property={property} isFirstCard={index === 0} headingLevel={2} />
            ))
          ) : (
            <div className="col-span-full text-center py-12">
              <p className="text-toka-text">No properties available at the moment.</p>
            </div>
          )}
        </div>
        {!isLoading && !error && properties && properties.length > 0 && (
          <div className="text-center mt-8">
            <button
              onClick={() => setVillaConfigOpen(true)}
              className="text-[#AD6433] hover:text-[#8B5025] underline font-medium transition-colors"
            >
              View Villa Configurations
            </button>
          </div>
        )}
      </div>
      <VillaConfigModal isOpen={villaConfigOpen} onClose={() => setVillaConfigOpen(false)} />
    </section>
  );
}